import React, { useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import {
  FaCircleCheck,
  FaCircleExclamation,
  FaTriangleExclamation,
  FaCircleInfo,
} from 'react-icons/fa6';
import './Toast.less';

// The one toast: status icon | message | close.
//
// The status icon is never a cross. Error toasts used the same close glyph as
// the close button, so they read "x message x" -- two apparent close controls.
// There is exactly one close button, owned here; `dismissible: false` renders
// none.
//
// Types callers use: success, error, warning, info; 'danger' is an alias of
// error. Previously only success and error had colours -- warning and info
// rendered white text on no background.
const ICONS = {
  success: FaCircleCheck,
  error: FaCircleExclamation,
  warning: FaTriangleExclamation,
  info: FaCircleInfo,
};

export function toastKind(type) {
  if (type === 'danger') return 'error';
  return ICONS[type] ? type : 'info';
}

const Toast = ({ message, type = 'success', duration = 4000, onClose, details, dismissible = true }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  if (!message) return null;

  const kind = toastKind(type);
  const Icon = ICONS[kind];
  const urgent = kind === 'error' || kind === 'warning';

  return (
    <div
      className={`gc-toast gc-toast--${kind}`}
      role={urgent ? 'alert' : 'status'}
      data-toast={kind}
    >
      <Icon className="gc-toast__icon" aria-hidden="true" data-toast-icon={kind} />
      <div className="gc-toast__text" data-toast-text>
        <div className="gc-toast__message">{message}</div>
        {details && <div className="gc-toast__details">{details}</div>}
      </div>
      {dismissible && (
        <button type="button" className="gc-toast__close" onClick={onClose} aria-label="Close notification">
          <IoMdClose aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default Toast;
